import type { ReactNode } from 'react';
import { Box, Button } from '@mui/material';
import type { BoltConfiguration } from '../../../shared/types';
import type { UndoPlanAffordance } from '../../../shared/lib/parameterUndo/undoAffordance';
import type { OperationProgressModel } from '../../../shared/ui/progress';
import MainLayout from '../../../shared/ui/layout/MainLayout';
import { CaptionText } from '../../../shared/ui/typography';
import {
  ActiveSectionHeader,
  EditorSurface,
  type SectionSummaryItem,
  SectionSwitcher,
  WorkflowNotice,
  WorkflowStepShell,
} from '../../../shared/ui/workflow/WorkflowStepComponents';
import ParameterSetupScopePanel, {
  ProjectCategoryToggleStrip,
} from '../../../shared/ui/workflow/ParameterSetupScopePanel';
import WorkflowAvailabilityProgress from './WorkflowAvailabilityProgress';
import ActionHelpTooltip from '../../../shared/ui/common/ActionHelpTooltip';
import HelpTooltipTrigger from '../../../shared/ui/common/HelpTooltipTrigger';
import { ValidationNavigationNotice } from '../../pcf-validation';
import { borderRadius, layoutGap } from '../../../theme/theme';
import { boltConnectionTooltips } from '../../../shared/config/wizardTooltips';
import { BOLT_PROJECT_PARAMETER_CATEGORY_OPTIONS } from '../../../shared/types';
import WizardSnackbarAlert from '../../../shared/ui/workflow/WizardSnackbarAlert';
import CompactFieldRow from '../../../shared/ui/workflow/CompactFieldRow';
import { MappingFieldGrid } from '../../../shared/ui/workflow/WorkflowStepComponents';
import { BOLT_FIELD_DATA_TYPES } from '../../../shared/services/boltParameterSetup';
import type { ParameterOption, SharedParameterDataType } from '../../../shared/types/sharedParameter.types';
import {
  BOLT_BASE_FAMILY_OPTIONS,
  type BoltFieldDefinition,
  type BoltFieldKey,
  type Notification,
} from '../lib/boltConnectionModel';

interface ActiveSectionState {
  key: string;
  title: string;
  description: string;
}

export interface BoltConnectionPageViewProps {
  isProcessing: boolean;
  progressModel: OperationProgressModel | null;
  isFinalizingConnectorParameters: boolean;
  connectorFinalizationProgress: OperationProgressModel | null;
  boltTargetApplication: string;
  onGoBack: () => void;
  onContinue: () => void;
  nextText: string;
  canProceed: boolean;
  footerContent: ReactNode;
  isCheckingAvailability: boolean;
  availabilityProgress: OperationProgressModel | null;
  showValidationErrors: boolean;
  validationIsValid: boolean;
  missingFields: string[];
  setupConfirmed: boolean;
  autoConfirmed: boolean;
  config: BoltConfiguration;
  onTargetChange: (target: {
    destination: 'project' | 'family';
    bindingKind: 'type' | 'instance';
  }) => void;
  onConfirmedChange: (confirmed: boolean) => void;
  isGasketMode: boolean;
  modelingOptionLabel: string;
  onToggleModelingChoice: () => void;
  hasUndoableBoltAddition: boolean;
  boltUndoAffordance: UndoPlanAffordance;
  onUndoLastAddition: () => void;
  onProjectCategoryChange: (category: string, checked: boolean) => void;
  sectionSummaryItems: SectionSummaryItem[];
  activeSectionState: ActiveSectionState;
  activeSectionStatusLabel: string;
  activeSectionStatusTone: 'ready' | 'missing' | 'optional';
  activeFields: readonly BoltFieldDefinition[];
  boltBaseFamilyValue: string;
  yesNoParameterOptions: ParameterOption[];
  parameterOptionsByDataType: Partial<Record<SharedParameterDataType, ParameterOption[]>>;
  textParameterOptions: ParameterOption[];
  fieldHandlers: Record<
    Exclude<BoltFieldKey, 'boltBaseFamilyValue' | 'boltModelingChoice'>,
    (value: string) => void
  >;
  updateField: (field: BoltFieldKey, value: string) => void;
  getFieldError: (field: BoltFieldKey) => string | undefined;
  isValidationFocused: (...tokens: string[]) => boolean;
  hasSharedParameterOptions: boolean;
  notification: Notification;
  onNotificationClose: () => void;
}

export function BoltConnectionPageView(props: BoltConnectionPageViewProps) {
  return (
    <>
      <MainLayout
        title="Bolts"
        maxWidth="95vw"
        maxHeight="92vh"
        showBack
        onBack={props.onGoBack}
        showNext
        onNext={props.onContinue}
        nextText={props.nextText}
        nextDisabled={!props.canProceed}
        compactWorkflowChrome
        workflowChromeVariant="wizard"
        showRuntimeBadgeInUtilityChrome={false}
        footerContent={props.footerContent}
      >
        <ValidationNavigationNotice applicableTargetPages={['properties']} />
        <WorkflowStepShell sx={{ width: '100%', py: 0.75 }}>
          <WorkflowAvailabilityProgress
            visible={props.isCheckingAvailability}
            suppressed={props.isProcessing}
            model={props.availabilityProgress}
          />
          {props.showValidationErrors && !props.validationIsValid && (
            <WorkflowNotice severity="warning" title="Complete the required bolt mappings">
              <CaptionText>Missing mappings: {props.missingFields.join(', ')}</CaptionText>
            </WorkflowNotice>
          )}
          <Box sx={{ display: 'grid', gap: layoutGap.row, minWidth: 0 }}>
            <ParameterSetupScopePanel
              layout="minimal"
              eyebrow=""
              title="Create missing bolt parameters"
              description="Choose where Revit stores bolt parameters and whether their values are defined by TYPE or INSTANCE. The selected PCF strategy determines which mappings are required below."
              statusLabel={props.setupConfirmed ? (props.autoConfirmed ? 'Ready' : 'Set') : 'Missing'}
              statusTone={props.setupConfirmed ? 'set' : 'missing'}
              blocker={!props.setupConfirmed ? 'Continue blocked: confirm creation of missing Revit parameters' : null}
              target={{
                destination: props.config.parameterDestination,
                bindingKind: props.config.parameterBindingKind,
              }}
              recommendedTarget={{ destination: 'project', bindingKind: 'instance' }}
              onTargetChange={props.onTargetChange}
              confirmed={props.setupConfirmed}
              autoConfirmed={props.autoConfirmed}
              onConfirmedChange={props.onConfirmedChange}
              modeLegend="Bolt parameter mode"
              modeAriaLabel="Bolt parameter mode"
              setupHelp={boltConnectionTooltips.setupPanel}
              modeHelp={(
                <HelpTooltipTrigger
                  label="Bolt parameter mode choices"
                  help={boltConnectionTooltips.parameterMode}
                  placement="bottom-start"
                />
              )}
              action={(
                <ActionHelpTooltip help={boltConnectionTooltips.switchModelingChoice} placement="bottom-start">
                  <Button
                    size="small"
                    variant="outlined"
                    onClick={props.onToggleModelingChoice}
                    sx={{ px: 1, minHeight: 26, borderRadius: `${borderRadius.md}px` }}
                  >
                    {props.modelingOptionLabel}
                  </Button>
                </ActionHelpTooltip>
              )}
              notice={props.boltTargetApplication}
              noticeHelpAsIcon
              noticeHelp={(
                <HelpTooltipTrigger
                  label={props.isGasketMode ? 'Gasket parameter target' : 'Bolt parameter target'}
                  help={props.isGasketMode
                    ? boltConnectionTooltips.gasketTargetBehavior
                    : boltConnectionTooltips.targetBehavior}
                  placement="bottom-start"
                />
              )}
              appliedStatus={{
                visible: props.hasUndoableBoltAddition,
                message: props.boltUndoAffordance.message,
                severity: props.boltUndoAffordance.severity,
                onRevert: props.boltUndoAffordance.canRevert
                  ? props.onUndoLastAddition
                  : undefined,
                revertHelp: boltConnectionTooltips.revertSetup,
              }}
            >
              {props.config.parameterDestination === 'project' ? (
                <ProjectCategoryToggleStrip
                  categories={BOLT_PROJECT_PARAMETER_CATEGORY_OPTIONS}
                  selectedCategories={props.config.projectParameterCategories ?? ['Pipe Accessories']}
                  onCategoryChange={props.onProjectCategoryChange}
                  help={boltConnectionTooltips.projectCategories}
                  helpLabel="Bolt project categories"
                />
              ) : null}
            </ParameterSetupScopePanel>
            <SectionSwitcher
              ariaLabel="Bolt configuration sections"
              items={props.sectionSummaryItems}
              presentation="tabs"
            />
            <EditorSurface sx={{ p: 1, minWidth: 0 }}>
              <Box
                role="tabpanel"
                id={`bolt-section-panel-${props.activeSectionState.key}`}
                aria-labelledby={`bolt-section-tab-${props.activeSectionState.key}`}
                sx={{ display: 'grid', gap: layoutGap.row }}
              >
                <ActiveSectionHeader
                  title={props.activeSectionState.title}
                  description={props.activeSectionState.description}
                  statusLabel={props.activeSectionStatusLabel}
                  statusTone={props.activeSectionStatusTone}
                />
                <MappingFieldGrid columns={{ xs: '1fr', md: 'repeat(2, minmax(0, 1fr))' }}>
                  {props.activeFields.map((field) => {
                    const isBoltSetActiveField = field.field === 'boltSetActiveValue';
                    const value = field.field === 'boltBaseFamilyValue'
                      ? props.boltBaseFamilyValue
                      : props.config[field.field].value;
                    const options = field.field === 'boltBaseFamilyValue'
                      ? BOLT_BASE_FAMILY_OPTIONS
                      : isBoltSetActiveField
                        ? props.yesNoParameterOptions
                        : props.parameterOptionsByDataType[
                            BOLT_FIELD_DATA_TYPES[field.field as keyof typeof BOLT_FIELD_DATA_TYPES]
                          ] ?? props.textParameterOptions;
                    const onChange = field.field === 'boltBaseFamilyValue'
                      ? (nextValue: string) => props.updateField('boltBaseFamilyValue', nextValue)
                      : props.fieldHandlers[
                          field.field as keyof typeof props.fieldHandlers
                        ];
                    return (
                      <CompactFieldRow
                        key={field.field}
                        label={field.label}
                        value={value}
                        onChange={onChange}
                        options={options}
                        help={field.help}
                        error={props.getFieldError(field.field)}
                        validationFocused={props.isValidationFocused(...field.focusTokens)}
                        required={field.required}
                        allowCustomValue={field.field !== 'boltBaseFamilyValue'}
                        disabled={field.readOnly}
                      />
                    );
                  })}
                </MappingFieldGrid>
              </Box>
            </EditorSurface>
            {!props.hasSharedParameterOptions && (
              <WorkflowNotice severity="info">
                Load shared parameter definitions to browse available names, or type exact Revit
                parameter names. New parameters use the data type required by each bolt field.
              </WorkflowNotice>
            )}
          </Box>
        </WorkflowStepShell>
      </MainLayout>
      <WizardSnackbarAlert
        open={props.notification.open}
        onClose={props.onNotificationClose}
        severity={props.notification.severity}
        title={props.notification.title}
      >
        {props.notification.message}
      </WizardSnackbarAlert>
    </>
  );
}
